import { useEffect, useState } from "react";
import { UserRound, Check, Plus, Pencil, Trash2, ChevronDown, Crown, Settings, LogOut } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "./ui/dropdown-menu";
import {
  fetchProfiles, createProfile, renameProfile, deleteProfile, activeProfile,
} from "../lib/api";
import { useAuth } from "../lib/auth";

export default function ProfileSwitcher({ variant = "desktop" }) {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const { signOut } = useAuth();

  async function load() {
    try { setData(await fetchProfiles()); } catch { /* not signed in */ }
  }
  useEffect(() => { load(); }, []);

  if (!data || !data.items?.length) return null;

  const activeId = activeProfile.get() || data.default_id;
  const active = data.items.find((p) => p.id === activeId) || data.items[0];

  function switchTo(id) {
    if (id === activeId) return;
    activeProfile.set(id === data.default_id ? "" : id);
    window.location.reload();
  }

  async function addProfile() {
    if (!data.can_add) {
      toast.error("Add more profiles with Pro.");
      navigate("/pro");
      return;
    }
    const name = window.prompt("Name this profile (e.g. My daughter)");
    if (!name || !name.trim()) return;
    try {
      const p = await createProfile(name.trim());
      toast.success(`Added “${p.name}”`);
      await load();
    } catch (e) {
      if (e?.response?.status === 402) { toast.error("Profiles beyond one are a Pro feature."); navigate("/pro"); }
      else toast.error(e?.response?.data?.detail || "Couldn't add profile");
    }
  }

  async function rename(p) {
    const name = window.prompt("Rename profile", p.name);
    if (!name || !name.trim() || name === p.name) return;
    try { await renameProfile(p.id, name.trim()); await load(); toast.success("Renamed"); }
    catch { toast.error("Couldn't rename"); }
  }

  async function remove(p) {
    if (!window.confirm(`Delete “${p.name}” and its saved routines & tracking?`)) return;
    try {
      await deleteProfile(p.id);
      if (p.id === activeId) activeProfile.set("");
      toast.success("Profile deleted");
      if (p.id === activeId) window.location.reload();
      else await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete"); }
  }

  const triggerCls = variant === "desktop"
    ? "hidden md:inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-full bg-cream-100 border border-surface-line text-ink-soft hover:text-ink transition-colors"
    : "w-full flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-cream-100 text-[15px] text-ink-soft min-h-[48px]";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="profile-switcher" className={triggerCls}>
        <span className="inline-flex items-center gap-1.5 min-w-0">
          <UserRound className="w-3.5 h-3.5 shrink-0" />
          <span className="truncate max-w-[120px] font-medium text-ink">{active?.name}</span>
        </span>
        <ChevronDown className="w-3.5 h-3.5 opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 z-[70]">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-ink-mute">
          Profiles {data.is_pro ? "" : "(Free: 1)"}
        </DropdownMenuLabel>
        {data.items.map((p) => (
          <DropdownMenuItem key={p.id} data-testid={`profile-item-${p.id}`}
            className="flex items-center gap-2 cursor-pointer"
            onSelect={(e) => { e.preventDefault(); switchTo(p.id); }}>
            <span className="w-4">{p.id === activeId && <Check className="w-4 h-4 text-safe-deep" />}</span>
            <span className="flex-1 truncate">{p.name}</span>
            {!p.is_default && (
              <>
                <button onClick={(e) => { e.stopPropagation(); rename(p); }} className="text-ink-mute hover:text-ink" aria-label="rename">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={(e) => { e.stopPropagation(); remove(p); }} className="text-ink-mute hover:text-avoid-deep" aria-label="delete">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="profile-add" onSelect={(e) => { e.preventDefault(); addProfile(); }}
          className="cursor-pointer flex items-center gap-2 text-ink">
          {data.can_add ? <Plus className="w-4 h-4" /> : <Crown className="w-4 h-4 text-[#a87a32]" />}
          {data.can_add ? "Add a profile" : "Add a profile (Pro)"}
        </DropdownMenuItem>
        {variant === "desktop" && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-ink-mute">Account</DropdownMenuLabel>
            <DropdownMenuItem data-testid="profile-account" onSelect={() => navigate("/account")}
              className="cursor-pointer flex items-center gap-2 text-ink">
              <Settings className="w-4 h-4" /> Your account
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="profile-signout" onSelect={() => signOut()}
              className="cursor-pointer flex items-center gap-2 text-avoid-deep">
              <LogOut className="w-4 h-4" /> Sign out
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
